import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import { ApiFn, BaseReq, useApi } from '#/api/fetch-api';
import { getSystemConfig, getSystemState } from '#/api/system';

import InProgressIcon from '#/assets/InProgressIcon';

import { Error } from '#/components/common/alert';

import useEffectOnce from '#/utils/useEffectOnce';
import { getInsuranceFund } from '#/api/insurance-fund';
import { c14142E, cFFFFFF } from '#/colors';

export default function Status() {
  return (
    <Page>
      <Container>
        <Header>
          <Heading>Status</Heading>
        </Header>
        <Body>
          <Response name="System State" api={getSystemState} />
          <Response name="System Config" api={getSystemConfig} />
          <Response name="Insurance" api={getInsuranceFund} />
        </Body>
      </Container>
    </Page>
  );
}

function Response<R extends BaseReq, T>({
  name,
  api,
  req = {} as R,
}: {
  readonly name: string;
  readonly api: ApiFn<R, T>;
  readonly req?: R;
}) {
  const [state, exec] = useApi(api);

  useEffectOnce(() => {
    exec(req);
  });

  if (state.isLoading || state.resp === null)
    return (
      <Block title={name}>
        <InProgressIcon />
      </Block>
    );

  if (state.resp.ok)
    return (
      <Block title={name}>
        <Pre>{JSON.stringify(state.resp.data, null, 2)}</Pre>
      </Block>
    );

  return (
    <Block title={name}>
      <Error>Failed to load {name}</Error>
    </Block>
  );
}

function Block(props: PropsWithChildren<{ readonly title: string }>) {
  return (
    <Card>
      <CardTitle>{props.title}</CardTitle>
      {props.children}
    </Card>
  );
}

const Page = styled.div`
  flex-grow: 1;
  padding: 0px 24px;
  width: 100%;
  overflow: scroll; /* avoid jumpy layout when container becomes scrollable */
`;

const Container = styled.div`
  margin: 0 auto;
  max-width: 1100px;
  width: 100%;
  min-height: 100%;
`;

const Header = styled.div`
  margin-top: 44px;
  margin-bottom: 24px;
`;

const Heading = styled.h1`
  font-size: 24px;
  line-height: 24px;
  margin: 0;
  font-weight: 700;
  align-self: flex-end;
`;

const Body = styled.div`
  display: grid;
  grid-template-rows:
    auto
    auto
    auto
    1fr;

  gap: 24px;
`;
export const Card = styled.div`
  background-color: ${c14142E};
  border-radius: 7px;
  display: grid;
  font-weight: 500;
  gap: 21px;
  padding: 16px 21px 19px;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: 13px;
  line-height: 12px;
  font-weight: 500;
  opacity: 0.7;
`;

const Pre = styled.pre`
  background: ${rgba(cFFFFFF, 0.03)};
  border-radius: 7px;
  color: ${rgba(cFFFFFF, 0.8)};
  font-size: 12px;
  line-height: 16px;
  margin: 0;
  padding: 16px;
  overflow-x: auto;
`;
